/** Connector-decided track-end paths that bypass the web wall-clock guard (#11207). */

import { diagnosticLog } from '../utils/diagnosticLogger';
import {
  evaluateWallClockAdvanceGuard,
  getEffectiveStartOffsetSecs,
  getSincePlayUriMs,
  isAuthoritativeConnectorTrackEndSource,
  isSuppressWallClockAdvanceActive,
  START_GRACE_MS,
} from './positionReconcile';
import { TransportCommand } from './TransportCommand';

/**
 * SetNext boundary routes where the Connector already judged the end: the renderer
 * stopped/never fetched the prefetched next track, so the queue must advance (#11207).
 */
const BOUNDARY_DECIDED_TRACK_END_SOURCES = new Set(['prefetch-never-reached']);

export function isBoundaryDecidedTrackEndSource(source: string | null | undefined): boolean {
  return !!source && BOUNDARY_DECIDED_TRACK_END_SOURCES.has(source);
}

export type TrustedTrackEndInput = {
  source: string | undefined;
  path: string;
  rendererId: string;
  trackId: string | null;
  durationSecs: number | null;
  position: number;
  clearStalledStartWatch: () => void;
};

export function allowTrustedConnectorTrackEnd(input: TrustedTrackEndInput): boolean {
  if (isSuppressWallClockAdvanceActive()) return false;
  const base = { path: input.path, trackId: input.trackId, rendererId: input.rendererId };

  // A web-side clock reset with no real playUri (selection/adopt paths) must not read as a
  // fresh start; a real playUri inside the grace window means the boundary event is stale.
  if (
    isBoundaryDecidedTrackEndSource(input.source)
    && !TransportCommand.isPlayUriRecent(START_GRACE_MS)
  ) {
    diagnosticLog('info', 'playback', '[playback] advance reason=boundary_decided_track_end', {
      ...base,
      source: input.source,
      position: input.position,
      duration: input.durationSecs,
      sincePlayUri: getSincePlayUriMs(),
    });
    input.clearStalledStartWatch();
    return true;
  }

  if (!isAuthoritativeConnectorTrackEndSource(input.source)) return false;
  const guard = evaluateWallClockAdvanceGuard({
    durationSecs: input.durationSecs ?? 0,
    sincePlayUriMs: getSincePlayUriMs(),
    effectiveStartOffsetSecs: getEffectiveStartOffsetSecs(),
    // Position-dead renderers: judge end from wall clock only, not stale/zero RelTime.
    positionSecs: input.durationSecs ?? input.position,
    rendererState: 'STOPPED',
  });
  if (!guard.allow) return false;
  diagnosticLog('info', 'playback', '[playback] advance reason=authoritative_track_end', {
    ...base,
    position: input.position,
    duration: input.durationSecs,
    sincePlayUri: getSincePlayUriMs(),
    wallClockEnd: guard.wallClockEnd,
    forcedAtFrozenEnd: guard.forcedAtFrozenEnd,
    priorSuppressionCount: guard.priorSuppressionCount,
  });
  input.clearStalledStartWatch();
  return true;
}
